<script setup lang="ts">
/**
 * The tasks tile: the top-right slot, and the one that changes shape most (IPD-2977 11.1).
 *
 * Six faces, as the ticket enumerates them: the intro video, the task list, the same list once
 * everything is done ("all caught up"), and three discovery promos — which are one layout shown
 * with three sets of copy, not three faces.
 *
 * It is the overflow of the Upcoming tile: both render the same aggregation with the same card,
 * so the two can never describe the same task differently. Nothing here decides what to show —
 * the face arrives resolved from the backend (IPD-2970), which also owns the precedence between
 * video, list, all-caught-up and promo, and the promo rotation.
 */
import {computed, ref, watch} from 'vue';
import type {RouteLocationRaw} from 'vue-router';
import TodayItemCard from '@/vue/molecules/today/today-item-card.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import LightboxVideo from '@/vue/molecules/lightbox-video.vue';
import {useWatchedReport} from '@/ts/composables/stateful/use-watched-report';
import {useTourStore} from '@/ts/store/tour-store';
import {usePresentedReport} from '@/ts/composables/stateful/use-presented-report';
import {TOUR_NAMES} from '@/ts/utils/tour-constants';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {
  type TodayCardItem,
  TODAY_TASK_LIST_MAX,
  TodayPromoKind,
  TodayTasksFace,
  type TodayTasks,
  type TodayTask,
} from '@/ts/types/component/today-page';
import {todayPromoBodyKey, todayPromoTitleKey} from '@/ts/composables/pure/today-label-keys';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {pagesPath} from '@/ts/router/pages-path';
import challengeT1 from '@/assets/img/today-promo/challenge-t1.webp';
import challengeT2 from '@/assets/img/today-promo/challenge-t2.webp';
import challengeT3 from '@/assets/img/today-promo/challenge-t3.webp';
import challengeM1 from '@/assets/img/today-promo/challenge-m1.webp';
import challengeM2 from '@/assets/img/today-promo/challenge-m2.webp';
import challengeM3 from '@/assets/img/today-promo/challenge-m3.webp';
import challengeB1 from '@/assets/img/today-promo/challenge-b1.webp';
import challengeB2 from '@/assets/img/today-promo/challenge-b2.webp';
import challengeB3 from '@/assets/img/today-promo/challenge-b3.webp';
import medical1 from '@/assets/img/today-promo/medical-1.webp';
import medical2 from '@/assets/img/today-promo/medical-2.webp';
import medical3 from '@/assets/img/today-promo/medical-3.webp';
import medical4 from '@/assets/img/today-promo/medical-4.webp';
import prevention1 from '@/assets/img/today-promo/prevention-1.webp';
import prevention2 from '@/assets/img/today-promo/prevention-2.webp';
import prevention3 from '@/assets/img/today-promo/prevention-3.webp';

const props = defineProps<{
  tile: TodayTasks;
}>();

const emit = defineEmits<{
  /** A row was activated. Modal tasks rely on this; the rest are links and navigate themselves. */
  selectTask: [task: TodayCardItem];
  /**
   * The promo's own CTA was taken (IPD-2974). Announced although it is a link, like everything
   * else on this page that navigates by itself: this is the one control on the promo face, so
   * without it the face would report as seen and never as acted on. Which of the three promos it
   * was is the tile's state, not this event's business.
   */
  browse: [];
  /**
   * The welcome video reached its halfway mark (IPD-2974), carrying the piece it was.
   *
   * Announced as well as reported to the backend, and the two are not the same thing: the report
   * is what closes the video's window, and this says the tile is where it happened — which the
   * backend's own event cannot, since it carries the piece but not the surface.
   */
  videoWatched: [contentId: UuidDTO];
}>();

/**
 * The rows to draw. The backend already caps the overflow at five (IPD-2970 4.4); the slice is
 * a guard so a payload that outgrows the design cannot stretch the tile out of its row.
 */
const tasks = computed<TodayTask[]>(() =>
  props.tile.face === TodayTasksFace.taskList ? props.tile.tasks.slice(0, TODAY_TASK_LIST_MAX) : []);

/**
 * Where the promo's button goes. Only the challenge one has a place of its own on Explore; medical
 * and prevention advertise a catalogue the page has no section for, so they still open it at the
 * top.
 */
const promoDestination = computed<RouteLocationRaw>(() =>
  (props.tile.face === TodayTasksFace.promo && props.tile.promo === TodayPromoKind.challenge
    ? {path: pagesPath.main.explore, hash: '#explore-challenges'}
    : pagesPath.main.explore));

const tourStore = useTourStore();
const {reportPresented, hasReportedPresented} = usePresentedReport();

/**
 * The welcome video plays itself, muted, until the backend says it has already been put in front
 * of this reader (IPD-3105). Three conditions. The backend owns the once-only part through
 * `autoplay`, because nothing on this side survives a logout; the tour owns the timing — both land
 * on the same first login, and a video started under its overlay plays to nobody, so this waits for
 * `shouldShowTour` to turn false, which is the moment it closes.
 *
 * The third is what the backend cannot answer inside a single session. Its flag arrives on a
 * payload, and this page re-fetches: finishing a questionnaire calls `load()`, which unmounts the
 * bento behind the loading state and builds a new player with its own once-per-mount autoplay. If
 * that payload still says `autoplay` — the GET raced the report, or the report failed — the video
 * starts a second time in one sitting. What we have already put in front of this reader is
 * remembered for the session, so ask that too. Only for this document: a second tab keeps its own
 * store and has to fall back to the flag, which is the backend's answer and the authority anyway.
 */
const shouldAutoplayWelcome = computed(() =>
  props.tile.face === TodayTasksFace.video
  && props.tile.autoplay
  && !hasReportedPresented(props.tile.contentId)
  && !tourStore.shouldShowTour(TOUR_NAMES.onboardingTour));

/**
 * Reported when the video actually started rather than when we asked it to: an autoplay the browser
 * refused has presented nothing and should get its turn again next time.
 *
 * Keyed on the gate being open, which is not quite the same as the playback being ours. With the
 * gate open and the autoplay refused — a browser policy, or a reader who asked for reduced motion —
 * the reader's own tap records a presentation and spends the once. Deliberate: the video did reach
 * them, and the alternative is autoplaying at someone who has already seen it.
 */
function onWelcomePlaybackStarted(): void {
  if (props.tile.face === TodayTasksFace.video && shouldAutoplayWelcome.value) {
    reportPresented(props.tile.contentId);
  }
}

const promoTitleKey = computed<string | null>(() =>
  (props.tile.face === TodayTasksFace.promo ? todayPromoTitleKey[props.tile.promo] : null) ?? null);

/** Each promo paints the tile its own colour, so the three are told apart at a glance. */
const promoSurfaceClass = computed<string | null>(() => {
  if (props.tile.face !== TodayTasksFace.promo) {
    return null;
  }
  const surfaces: Record<TodayPromoKind, string> = {
    [TodayPromoKind.challenge]: 'tasks-tile--promo-challenge',
    [TodayPromoKind.medicalProgram]: 'tasks-tile--promo-medical',
    [TodayPromoKind.preventionProgram]: 'tasks-tile--promo-prevention',
  };

  return surfaces[props.tile.promo] ?? null;
});

/**
 * The plain face paints itself as well (IPD-3129): the design gives the task list a steel surface
 * and the celebration a sage one, so the pair of tiles at the top of the page stop being two
 * beige rectangles side by side.
 *
 * Keyed off what the tile RENDERS rather than off the face it was handed. The celebration also
 * stands in for a `taskList` face that arrived with no rows — the branch below it in the template
 * — and that degenerate state has to come out green like the real `allCaughtUp` one, not steel.
 */
const plainSurfaceClass = computed<string | null>(() => {
  // Named rather than "whatever is left after video and promo": a seventh face added to the enum
  // would otherwise fall in here and come out sage, the same way the promo maps above are total
  // Records so that adding a promo kind fails compilation instead of rendering untinted.
  if (props.tile.face !== TodayTasksFace.taskList && props.tile.face !== TodayTasksFace.allCaughtUp) {
    return null;
  }

  return tasks.value.length > 0 ? 'tasks-tile--list' : 'tasks-tile--caught-up';
});

const promoBodyKey = computed<string | null>(() =>
  (props.tile.face === TodayTasksFace.promo ? todayPromoBodyKey[props.tile.promo] : null) ?? null);

/**
 * The artwork each promo is drawn with, in the order the layout below places it. Imported one by
 * one rather than resolved from a directory: that is what the rest of the repo does with bundled
 * artwork (see admin-onboarding-tour.vue), and it is what makes a renamed or deleted file break
 * the build instead of silently leaving a promo bare.
 */
const ARTWORK_BY_PROMO: Record<TodayPromoKind, string[]> = {
  // A 3x3 honeycomb of photos, bottom right — the CSS cuts them into rounded hexagons and lets
  // the tile's overflow do the edge bleed (see the art rules for why). Listed top row, middle
  // row, bottom row — the suffixes carry the grid the one-per-line rule forbids writing out.
  [TodayPromoKind.challenge]: [
    challengeT1,
    challengeT2,
    challengeT3,
    challengeM1,
    challengeM2,
    challengeM3,
    challengeB1,
    challengeB2,
    challengeB3,
  ],
  // Four circles under the copy: stacked at rest, dealing out into a row on hover.
  [TodayPromoKind.medicalProgram]: [medical1, medical2, medical3, medical4],
  // Three rounded cards fanned out, bleeding past the bottom edge.
  [TodayPromoKind.preventionProgram]: [prevention1, prevention2, prevention3],
};

const promoArtwork = computed<string[]>(() =>
  (props.tile.face === TodayTasksFace.promo ? ARTWORK_BY_PROMO[props.tile.promo] : null) ?? []);

// Through a typed Record like the surface class above: renaming a TodayPromoKind MEMBER then
// fails compilation here (a value rename compiles — computed keys — and is harmless now that
// the class names no longer interpolate it), and the unit test pins each literal against the
// element, since the positioning rules key off these names.
const ART_CLASS_BY_PROMO: Record<TodayPromoKind, string> = {
  [TodayPromoKind.challenge]: 'tasks-tile__art--challenge',
  [TodayPromoKind.medicalProgram]: 'tasks-tile__art--medicalProgram',
  [TodayPromoKind.preventionProgram]: 'tasks-tile__art--preventionProgram',
};

const promoArtClass = computed<string | null>(() =>
  props.tile.face === TodayTasksFace.promo ? ART_CLASS_BY_PROMO[props.tile.promo] ?? null : null);

const posterUrl = computed<string | null>(() =>
  props.tile.face === TodayTasksFace.video ? props.tile.posterUrl : null);

/**
 * Set once the player starts: it takes the gradient and the copy off the frame.
 *
 * Measured on the design's prototype rather than assumed — playing there leaves the player alone in
 * the tile, with no gradient and no words over it. Both belong to the resting state: the gradient
 * is there so the fixed light copy holds on a poster nobody controls, so once the copy goes the
 * gradient has nothing left to do, and neither has any business over a frame the user is watching.
 */
const playing = ref(false);

// Back to false when the tile is handed a different face. The flag outlived the face it belongs to:
// a payload that put another face here and then came back to the video rendered a player that had
// never started with neither gradient nor title over it, since both are drawn on the strength of
// this flag.
//
// The face and not the view-model it arrives in. A reload hands this tile a new object every time,
// and on a payload that still says video the player is the same one, still playing — putting the
// gradient and the title back over it is the very thing this is here to prevent.
watch(() => props.tile.face, () => {
  playing.value = false;
});

// And when a video that played itself finishes: it rewinds to its first frame, so the tile is back
// at rest and its copy belongs over it again. Only the autoplay ends this way — a playback the
// reader started keeps the frame to itself. Without this the face never changes, so a first-login
// reader was left an untitled frozen frame for the rest of the visit.
function onAutoplayEnded(): void {
  playing.value = false;
}

// Watching the video here is what closes its window: from then on it lives in the FAQ (IPD-2980).
const {reportWatched} = useWatchedReport();

function onHalfWatched(contentId: UuidDTO): void {
  reportWatched(contentId);
  emit('videoWatched', contentId);
}
</script>

<template>
  <section
    class="tasks-tile"
    :data-face="tile.face"
    :class="[promoSurfaceClass, plainSurfaceClass, {'tasks-tile--video': tile.face === TodayTasksFace.video}]"
  >
    <template v-if="tile.face === TodayTasksFace.video">
      <!-- The video plays where it stands rather than sending the user to a page for it: that is
           what the design draws, and what the current home already does with this very piece (see
           featured-content-module.vue). The player fills the tile behind the copy and rests on the
           poster until it is started; without a poster the tile keeps its own dark surface.
           `is-light-boxed` is deliberately off — a tile this size is where the design plays it. -->
      <lightbox-video
        :autoplay-muted="shouldAutoplayWelcome"
        class="tasks-tile__video"
        :video-src="tile.videoUrl"
        :poster-src="posterUrl ?? undefined"
        :poster-alt="$t('today_tasks_video_title')"
        @playback-started="playing = true; onWelcomePlaybackStarted()"
        @half-watched="onHalfWatched(tile.contentId)"
        @autoplay-ended="onAutoplayEnded"
      />
      <span v-if="!playing" class="tasks-tile__scrim" aria-hidden="true"/>
      <!-- Over the player, and transparent to the pointer: the copy is a label on the frame, and
           a click anywhere on the tile belongs to the video underneath. -->
      <div v-if="!playing" class="tasks-tile__intro tasks-tile__intro--over-video">
        <span class="tasks-tile__eyebrow">{{ $t('today_tasks_video_eyebrow') }}</span>
        <h2 class="tasks-tile__heading">
          {{ $t('today_tasks_video_title') }}
        </h2>
      </div>
    </template>

    <template v-else-if="promoTitleKey && promoBodyKey">
      <div class="tasks-tile__intro">
        <span class="tasks-tile__eyebrow">{{ $t('today_tasks_promo_eyebrow') }}</span>
        <h2 class="tasks-tile__heading">
          {{ $t(promoTitleKey) }}
        </h2>
      </div>
      <p class="tasks-tile__body">
        {{ $t(promoBodyKey) }}
      </p>

      <!-- Decorative: the promo's meaning is entirely in the copy above. -->
      <div
        v-if="promoArtwork.length > 0"
        class="tasks-tile__art"
        :class="promoArtClass"
        aria-hidden="true"
      >
        <span
          v-for="(url, index) in promoArtwork"
          :key="url"
          class="tasks-tile__art-item"
          :style="{backgroundImage: `url(${url})`}"
          :data-index="index"
        />
      </div>
      <!-- The challenge promo names a section of Explore, so it goes to it rather than to the top
           of the page (IPD-3103); the other two advertise a catalogue the page has no section for.
           The section is always there to land on: the backend only puts this promo in the rotation
           when the reader has challenges to start, and it draws from the same catalogue as the rail
           through a stricter filter — it drops the ones already started, which the rail keeps. -->
      <balm-btn
        theme="inverse"
        paddings="none"
        icon="arrow-narrow-right.svg"
        icon-size="sm"
        :to="promoDestination"
        :label="$t('today_tasks_promo_cta')"
        class="tasks-tile__cta"
        @click="emit('browse')"
      />
    </template>

    <!-- The list and the celebration are the same face: both keep the heading and swap what
         sits underneath. Structurally this v-else is still the terminal branch — the tile keeps
         rendering whatever it is handed, and two tests pin that — but the MAPPER stopped feeding
         it malformed payloads: it yields no tile for those, so through the real pipeline the
         celebration's claim is only ever the backend's own ALL_CAUGHT_UP. -->
    <template v-else>
      <!-- Decorative, and only under the list: the design draws this shape on the steel tile and
           leaves the celebration's green one clean. -->
      <svg-illustration
        v-if="tasks.length > 0"
        name="today-tasks-ribbon.svg"
        class="tasks-tile__ribbon"
      />

      <h2 class="tasks-tile__heading">
        {{ $t('today_tasks_title') }}
      </h2>

      <ul v-if="tasks.length > 0" class="tasks-tile__list">
        <li v-for="task in tasks" :key="task.id" class="tasks-tile__row">
          <today-item-card :item="task" variant="row" @select="emit('selectTask', $event)"/>
        </li>
      </ul>

      <div v-else class="tasks-tile__done">
        <!-- The design system's "Empty list" piece, exported animated: it draws itself in on
             open, which is why it is not wrapped in any transition of ours. -->
        <svg-illustration name="today-all-caught-up.svg" class="tasks-tile__done-art"/>
        <p class="tasks-tile__done-title">
          {{ $t('today_tasks_all_caught_up_title') }}
        </p>
        <p class="tasks-tile__done-body">
          {{ $t('today_tasks_all_caught_up_body') }}
        </p>
      </div>
    </template>
  </section>
</template>

<style scoped lang="scss">
// Padding, radius and heading voice come from the page tokens, so this tile keeps the same
// rhythm as the one beside it.
.tasks-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: var(--today-tile-padding);
  border-radius: var(--today-radius-section);
  background: var(--today-surface-raised);
  overflow: hidden;
}

// The two plain faces take a surface of their own (IPD-3129), so the task list no longer matches
// the tile beside it. That accounts for every face `TodayTasks` can express — the promos and the
// video paint themselves below — so `--today-surface-raised` on `.tasks-tile` above is now the
// default for a tile carrying no face class at all, and nothing else.
//
// Steel is the medical promo's surface too. Deliberate rather than overlooked: the tints are named
// for the colour and not for the face that wears one, exactly so a second face can reuse one, and
// the two are different states of the SAME tile — never on screen together. Raised with design
// alongside the other Today notes, since telling the promo apart from the list was the point of
// giving the promos their own surfaces in the first place.
.tasks-tile--list { background: var(--today-tint-steel); }
.tasks-tile--caught-up { background: var(--today-tint-sage); }

// The ribbon behind the task list (IPD-3129). The file is the design's own export and already
// carries the 30% and the roughened edge (a turbulence filter) that make it read as drawn rather
// than plotted, so nothing here restates either — only the colour is reached into, through the
// token that carries the dark scheme's exception.
//
// The export needed two fixes to draw at all, both worth raising with design rather than carrying
// here for ever:
//
//   its `feDisplacementMap` was exported with `width="100%" height="100%"`, and a filter
//   primitive's percentages resolve against the VIEWPORT, not the filter region — so the roughened
//   output was clipped to a box ending at x=186, y=97 and all that reached the tile was a small
//   wedge in the middle. Dropping the two attributes lets the primitive default to the filter
//   region, which is what the export meant;
//
//   and its viewBox was 352x189 while the stroke inside runs from x=-143 to x=530, so the file
//   cropped away most of its own drawing — including the arm the mock shows at the tile's left
//   edge. Opened to the filter region, which is the drawing's true extent. Fitted against the mock,
//   the tight crop matches it at 0.66 overlap and the full one at 0.92.
//
// The tile's overflow does the bleeding, as it does for the promo artwork.
// Every number is a percentage of the tile's WIDTH, including the vertical one: a percentage
// margin resolves against the containing block's width even when it moves an element down, which
// is what keeps the shape's size and its place in the composition tied to each other as the tile
// grows. A `top` percentage would resolve against the HEIGHT instead and slide the ribbon down a
// tall tile. The three were fitted against the design rather than guessed — best overlap of the
// file's own outline with the shape measured off the mock.
.tasks-tile__ribbon {
  position: absolute;
  top: 0;
  left: -14.5%;
  z-index: 0;
  width: 160%;
  margin-top: -10%;
  pointer-events: none;

  :deep(svg) {
    display: block;
    width: 100%;
    height: auto;

    [stroke='#F5F1EE'] { stroke: var(--today-ribbon-ink); }
  }
}

// The list and its heading sit over the ribbon. Needed because the ribbon is positioned and they
// are not: a positioned element paints above static siblings whatever its z-index, so without
// these two the shape would be drawn over the rows rather than under them.
.tasks-tile__heading,
.tasks-tile__list,
.tasks-tile__done {
  position: relative;
  z-index: 1;
}

// The three promos are told apart by their surface, not just by their copy.
.tasks-tile--promo-challenge { background: var(--today-tint-mauve); }
.tasks-tile--promo-medical { background: var(--today-tint-steel); }
.tasks-tile--promo-prevention { background: var(--today-tint-moss); }

// The video face is drawn dark in either theme: the copy sits over the poster, so it cannot follow
// the scheme without losing its contrast once the poster loads. It takes that pair from the
// scaffold rather than declaring its own, because the Explore card (IPD-2978) needs the same one
// for the same reason and the two must not drift apart.
.tasks-tile--video {
  justify-content: space-between;
  background: var(--today-surface-dark);
  color: var(--today-ink-on-dark);
}

// The player fills the tile behind the copy, which is where the design puts the frame. It draws its
// own poster and its own controls, so nothing here paints one.
.tasks-tile__video {
  position: absolute;
  inset: 0;
  z-index: 0;

  // The player rounds its own corners, and it has to be told which radius: left alone it takes
  // `--card-border-radius`, which is 12px, 16px from the medium breakpoint and 18px from the large
  // one, while the tile is 16px throughout — so the two agree at exactly one breakpoint. Before
  // this, the tile's own corner showed as a dark crescent outside the player's at every other width.
  //
  // Fed through the variable the child reads rather than by overriding its rule from outside, which
  // would tie on specificity and be settled by whatever order the bundle emits. The fallback is not
  // decoration: the token is scoped to `.today-page`, and a custom property that resolves to nothing
  // invalidates the whole declaration silently — a radius of 0 rather than the app's own.
  // `today-tile-placeholder.vue` writes it the same way.
  //
  // NOT also on the `video` element. The reason to put it there would be that a playing video is
  // composited on its own layer and escapes an ancestor's rounded `overflow: hidden` — tested rather
  // than assumed, in the browser this suite runs: two identical rounded containers playing the same
  // file, one with a radius on the video and one without, over a colour that would show any
  // unclipped pixel. Both clipped, identically. So the rule bought nothing here while costing a
  // second one to undo it in fullscreen, where it rounded the corners of a black viewport. Worth
  // revisiting only with a real device in hand: hardware video overlay, iOS in particular, is where
  // that behaviour is reported, and is what a headless browser cannot exercise.
  --card-border-radius: var(--today-radius-section, 16px);

  :deep(video) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.tasks-tile__intro--over-video {
  position: relative;
  z-index: 1;
  pointer-events: none;
}

// The shade over the player, no longer the prototype's own: that one had three stops weighted the
// other way and shaded the bottom edge as well, which spent picture where there is no copy at all.
// This is a content card's shade mirrored — the copy sits at the TOP here — and it is there so the
// fixed light copy holds on a frame nobody controls, not to mark the piece as seen: that is the
// content tiles' dimming, which means something else and is not this.
//
// Same rule as the content card and the same floor (see `today-copy-scrim`): 4.5:1 for the 10px
// eyebrow, which is the smallest copy here and therefore what sets the weights. Measured over a
// white frame, worst position 4.71:1. Its own stops rather than the card's because the copy block
// is a different shape — the tile's padding is larger and the heading is one 28px line, so the
// band has less to cover and can clear sooner.
//
// This tile was BELOW that floor before this ticket touched it, at roughly 3.2-4.0 for the eyebrow
// and 2.1-3.2 for the heading against the prototype's percentage-anchored gradient. Fixed here
// rather than left, since it is the same two-line change on the same surface.
//
// `pointer-events: none` is the whole point and the prototype sets it too. Without it the gradient
// covered the play button, which is the only control this face has: the press landed on the
// gradient, the player never heard it and the video would not start.
.tasks-tile__scrim {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  @include today-copy-scrim(to bottom, 70% 0, 68% 110px, 30% 160px, 0 215px);
}

// Everything above the poster, which is the only positioned child behind them.
.tasks-tile__intro,
.tasks-tile__body,
.tasks-tile__cta {
  position: relative;
  z-index: 1;
}

.tasks-tile__intro {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
}

.tasks-tile__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-eyebrow);
}

.tasks-tile__heading {
  @include font-today-tile-title;

  margin: 0;
}

// The design does not reach for a second grey here: it takes the same ink as the heading and
// drops its opacity, which keeps the line quiet on a tinted promo surface without picking a
// colour that fights the tint.
//
// The design's own value is 66%, which does not survive the six surfaces this line lands on: at
// 16px regular it needs 4.5:1, and 66% gives 3.57-4.46:1 over the three promo tints in both
// schemes and 4.2:1 on the plain tile in the light one. 84% is the first step that clears every
// one of them (worst case 4.7:1, moss in dark) and is still visibly quieter than the heading.
.tasks-tile__body {
  @include font-body;

  font-size: var(--today-text-body);
  margin: 0;
  color: color-mix(in srgb, var(--Neutral-UI-neutral-ui-100) 84%, transparent);
}

// The CTA sits at the bottom whatever the copy above it is, so the promos do not bounce the
// button around as the tile rotates between them. Everything else about it — shape, padding,
// hover, focus — is the shared button atom's.
// The design's button is a step smaller than the app's default and hugs its label more closely:
// 14px with 10/16 of padding, against the atom's 16px and the nearest padding preset's 12/32.
// Only those metrics are overridden — shape, colour, hover and focus stay the atom's, and the
// glyph is sized through the atom's own `icon-size` prop rather than from out here.
//
// Lifting the atom's 44px floor takes the button to ~36px. That still clears WCAG 2.2 AA target
// size (24px), but it is under the 44px the rest of the app draws, so it is a deliberate step
// down for this page and not an oversight — raised with design alongside the other Today notes.
.tasks-tile .tasks-tile__cta {
  @include font-body-sm-bold;

  // 14 is the smallest step the app's type scale has, and this token floors at the user's minimum
  // font size, so the control keeps growing when someone raises their text size.
  font-size: var(--today-text-cta);
  align-self: flex-start;
  min-height: unset;
  margin-top: auto;
  padding: 10px 16px;
  line-height: 1;
}

// Promo artwork. Positions are the prototype's, measured against its 428px-wide tile and anchored
// to the edges rather than to a corner offset, so a wider tile keeps the composition where the
// design put it. It sits under the copy and over the surface; the tile's overflow does the
// bleeding. The whole block is absent until the files exist.
.tasks-tile__art {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

// The pieces sit gathered at rest and open out while the pointer is over the tile, settling back
// when it leaves. Behaviour, timing and every number below are the design's own, read off the
// prototype: it declares `transition: transform 0.52s cubic-bezier(0.34, 1.56, 0.64, 1)` on each
// piece, and its nine boxes were measured in both states relative to the tile's bottom-right corner.
//
// There is deliberately NO `@media (hover: hover)` guard. An earlier version had one, because its
// rest state closed past zero and left touch devices looking at overlapping hexagons forever. The
// rest state is the design's now, a composition that stands on its own, so a device that never
// hovers simply keeps it — and the animation no longer vanishes in a browser reporting no hover,
// which is what made Chrome's responsive mode look broken.
//
// Animating `translate` rather than `transform` on purpose: the fanned prevention cards carry a
// rotation in `transform`, and animating that property would wipe the rotation out mid-flight.
.tasks-tile__art-item {
  position: absolute;
  display: block;
  background-position: center;
  background-repeat: no-repeat;
  transition: translate 0.52s cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.tasks-tile:hover .tasks-tile__art-item {
  translate: var(--art-open-x, 0) var(--art-open-y, 0);
}

// Nine hexagons in a honeycomb, bottom right, bleeding past both edges.
//
// The shape is a mask, not the picture. The nine files are hexagons already, but they are hexagons
// of the wrong proportion — 125x141 is 0.887 where a regular one is 0.866 — and their points are
// sharp. The design ignores all of that: it drops each photo into the SAME square box, fills it with
// `cover`, and cuts the shape with the rounded regular hexagon below, so the baked outline never
// shows. Copying that here fixes three things at once and removes a fourth.
//
// What it removes is the special handling of the three pieces the design exports pre-cut — a narrow
// one for the top right, a short row for the bottom. Those cuts are just different crops of a photo,
// not different shapes: every box is the same square and the tile's overflow does the cutting. Every
// attempt to honour the cuts in CSS put those three pieces out of the lattice and left one gap at
// 6px beside its neighbours at 11.
//
// Positions are transcribed from the prototype rather than derived — its tile is 428 wide against
// our 424, and everything is anchored to the bottom-right corner, so the numbers carry over. Rest
// values sit on the element; `--art-open-*` is the travel to the hovered state.
// The rounded regular hexagon the design cuts each photo with, copied from the prototype's own
// mask. The curves at the six vertices are what soften the points; a hexagon drawn with straight
// joins reads noticeably sharper beside it. Split across lines only to stay inside the line length.
$today-hex-path: "M 43.94 3.5 Q 50 0 56.06 3.5 L 87.24 21.5 Q 93.3 25 93.3 32 L 93.3 68"
  + " Q 93.3 75 87.24 78.5 L 56.06 96.5 Q 50 100 43.94 96.5 L 12.76 78.5 Q 6.7 75 6.7 68"
  + " L 6.7 32 Q 6.7 25 12.76 21.5 Z";
$today-hex-mask: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'"
  + " viewBox='0 0 100 100'%3E%3Cpath d='#{$today-hex-path}' fill='black'/%3E%3C/svg%3E";

.tasks-tile__art--challenge .tasks-tile__art-item {
  width: 58.5px;
  height: 58.5px;
  background-size: cover;
  // Prefixed as well, like the repo's other mask call sites: the build carries no autoprefixer and
  // the unprefixed longhands are recent. Where they are dropped the nine photos render as
  // overlapping squares showing their baked outlines, the exact artefact the mask exists to hide.
  mask-image: url($today-hex-mask);
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: url($today-hex-mask);
  -webkit-mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;

  &[data-index='0'] { right: 79.7px; bottom: 80.5px; --art-open-x: -6px; --art-open-y: -6px; }
  &[data-index='1'] { right: 23.6px; bottom: 80.5px; --art-open-y: -7px; }
  &[data-index='2'] { right: -32.5px; bottom: 80.5px; --art-open-x: 6px; --art-open-y: -6px; }
  &[data-index='3'] { right: 108.1px; bottom: 31.7px; --art-open-x: -8px; }
  &[data-index='4'] { right: 52px; bottom: 31.7px; }
  &[data-index='5'] { right: -4px; bottom: 31.7px; --art-open-x: 8px; }
  &[data-index='6'] { right: 79.7px; bottom: -16.3px; --art-open-x: -6px; --art-open-y: 6px; }
  &[data-index='7'] { right: 24.4px; bottom: -16.3px; --art-open-y: 7px; }
  &[data-index='8'] { right: -31.7px; bottom: -16.3px; --art-open-x: 6px; --art-open-y: 6px; }

  @for $i from 0 through 8 {
    &[data-index='#{$i}'] { transition-delay: #{$i * 0.035}s; }
  }
}

// Four circles in a row under the copy, aligned with the text. 51px wide on a 30px pitch, so at
// rest each overlaps the one before by 21px; on hover they deal out until 5px of overlap is left,
// the same gesture as the other two faces read sideways. The artwork is drawn a little larger than
// its circle, as the design has it.
// This row belongs to the column rather than over it: the design draws it between the copy and the
// button, so it takes a line of its own and the copy can never run into it. It used to be pinned
// 127px off the tile's bottom like the other two faces, and no offset could hold — the copy's
// height moves with the tile's width, so the collision moved with it (IPD-3104). Being in the flow
// also lets the tile grow when the copy is tall, which is what the row's grid does with the height.
// The items stay absolute inside it, which is what keeps the deal-out gesture on hover.
// The composition is three numbers, not six: the circle's size, and how much of it its neighbour
// covers at rest and once the row opens on hover. Every position below is derived from those, so
// resizing the circles keeps the overlaps the design chose instead of silently widening them —
// which is what writing the pitch and the travel out as their own literals invites. The gap above
// the row is a fourth, independent of the three: it is the design's spacing to the copy and does
// not move when the circles resize.
$medical-art-size: 51px;
$medical-art-overlap-rest: 21px;
$medical-art-overlap-open: 5px;
$medical-art-gap: 16px;
$medical-art-pitch: $medical-art-size - $medical-art-overlap-rest;
$medical-art-open-step: $medical-art-overlap-rest - $medical-art-overlap-open;

.tasks-tile__art--medicalProgram {
  position: relative;

  // The base rule pins every art layer to the tile with `inset: 0`. On a relatively positioned box
  // those offsets do nothing, so they are cleared rather than left reading as positioning.
  inset: auto;

  // One circle tall, and no taller: the row is the circles.
  height: $medical-art-size;

  // The design puts 16px above this row and holds it at every width — measured off the prototype's
  // own desktop tile. Restated as `ART_GAP` in today.spec.ts, which measures it: the two are
  // the same number and desync loudly, the sweep failing at every width.
  //
  // The column's gap is `--Spacing-spacing-4`, which is responsive and lands on
  // 12, 16 and 24 across the widths this tile is drawn at, so it matches in the middle and drifts
  // at both ends. Cancelled and restated rather than changed at the source: the gap is the tile's
  // rhythm for everything else in the column, and only the art row is measured this way.
  margin-top: calc(#{$medical-art-gap} - var(--Spacing-spacing-4));

  // The one item here whose content measures nothing, every child being absolute, so it is the one
  // a height constraint on the tile would take its space from — and the circles inside it, absolute
  // and anchored to its top, would not shrink with it: they would spill onto the button, which is
  // the bug this rule exists to prevent. Nothing squeezes it today, the row's track being
  // `max(340px, content)`, but nothing records that either.
  flex-shrink: 0;
}

.tasks-tile__art--medicalProgram .tasks-tile__art-item {
  width: $medical-art-size;
  height: $medical-art-size;
  top: 0;
  border-radius: 999px;
  background-size: 120%;

  // Laid from the row's own left edge — the tile's padding is already behind it — each circle a
  // pitch further along, and opening by one step more than the one before it. The first stays put
  // in both states, which is what anchors the row while the rest deal out to its right.
  @for $i from 0 through 3 {
    &[data-index='#{$i}'] {
      left: $medical-art-pitch * $i;
      --art-open-x: #{$medical-art-open-step * $i};
      transition-delay: #{$i * 0.045}s;
    }
  }
}

// Three rounded cards out of the bottom-right corner: gathered at rest, fanning open on hover —
// the same gesture as the honeycomb and the circles. Both compositions are the design's own; the
// rest values fold the old open travel in, and the open travel returns each card to the fanned
// position, so the two measured states are preserved with only their roles swapped.
.tasks-tile__art--preventionProgram .tasks-tile__art-item {
  border-radius: 8px;

  // These are photographs filling their card, so they crop rather than fit.
  background-size: cover;

  &[data-index='0'] {
    right: 60px;
    bottom: -44px;
    width: 135px;
    height: 168px;
    transform: rotate(-9deg);
    --art-open-x: -15px;
    --art-open-y: -3px;
  }

  &[data-index='1'] {
    right: 30px;
    bottom: -46px;
    width: 125px;
    height: 162px;
    transform: rotate(-2deg);
    --art-open-y: -10px;
  }

  &[data-index='2'] {
    right: -18px;
    bottom: -38px;
    width: 130px;
    height: 165px;
    transform: rotate(6deg);
    --art-open-x: 15px;
    --art-open-y: -3px;
  }

  @for $i from 0 through 2 {
    &[data-index='#{$i}'] { transition-delay: #{$i * 0.035}s; }
  }
}

.tasks-tile__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

// The hairline belongs to the list, not to the shared card: it separates rows, so the first
// one does not get a rule above it and the card stays reusable outside a list.
.tasks-tile__row + .tasks-tile__row {
  border-top: 1px solid var(--today-divider);
}

.tasks-tile__done {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--Spacing-spacing-3);
  text-align: center;
}

// The piece ships in the design system's neutral palette: dark ink and a cream fill. The fill used
// to be tinted with the palette's own Sage, which is what the design drew when the tile underneath
// was beige; the tile IS that sage now, so tinting the cards with it a second time would leave them
// invisible on it. They take the neutral the design draws them in instead — see the token, which
// carries the dark scheme's exception. The ink is tied to the text colour rather than the exported
// near-black, which the design keeps fixed and would all but vanish on the dark scheme's tile.
.tasks-tile__done-art {
  width: 132px;
  height: 132px;

  :deep(svg) {
    [fill='#F5F1EE'] { fill: var(--today-done-art-card) !important; }
    [stroke='#293139'] { stroke: var(--Neutral-UI-neutral-ui-100); }
    [fill='#293139'] { fill: var(--Neutral-UI-neutral-ui-100); }
  }
}

.tasks-tile__done-title {
  @include font-body-bold;

  font-size: var(--today-text-card-title);
  margin: 0;
}

// Two lines by measure, not by a break in the string: the design holds this line to 240px so it
// wraps under itself. The tone is a step quieter than the tile's body copy.
.tasks-tile__done-body {
  @include font-body-sm;

  font-size: var(--today-text-row-title);
  max-width: 240px;
  margin: 0;
  color: var(--Neutral-UI-neutral-ui-60);
}
</style>
